<template>
	<v-card>
		<!-- Snackbar -->
		<Snackbar
			@close-snackbar="snackbar.status = false"
			v-model="snackbar.status"
			:text="snackbar.text"
			:color="snackbar.color"
		/>

		<!-- Card title -->
		<v-card-title class="primary white--text"
			>Saved searches
			<v-spacer></v-spacer>
			<v-btn icon dark @click="closeIndex">
				<v-icon>mdi-close</v-icon>
			</v-btn>
		</v-card-title>

		<!-- Datatable -->
		<v-data-table
			:headers="headers"
			:items="savedSearches"
			:loading="isLoading.savedSearches"
			class="elevation-1"
			loading-text="Loading... Please wait"
		>
			<template v-slot:top>
				<!-- Show dialog -->
				<v-dialog v-model="dialogShow" max-width="60%">
					<v-card>
						<v-card-title class="primary white--text"
							>Search parameteres
							<v-spacer></v-spacer>
							<v-btn icon dark @click="closeShow">
								<v-icon>mdi-close</v-icon>
							</v-btn>
						</v-card-title>

						<v-card-text>
							<v-container>
								<v-row class="mt-1">
									<!-- Country  -->
									<v-col cols="12" sm="6" md="4" v-if="country">
										<v-chip label class="mr-2"><strong>Country</strong></v-chip>
										<span>{{ country }}</span>
									</v-col>

									<!-- ATC Code  -->
									<v-col cols="12" sm="6" md="4" v-if="atcCode">
										<v-chip label class="mr-2"><strong>ATC Code</strong></v-chip>
										<span>{{ atcCode }}</span>
									</v-col>

									<!-- VNR  -->
									<v-col cols="12" sm="6" md="4" v-if="vnr">
										<v-chip label class="mr-2"><strong>VNR</strong></v-chip>
										<span>{{ vnr }}</span>
									</v-col>

									<!-- Period  -->
									<v-col cols="12" sm="6" md="4" v-if="period">
										<v-chip label class="mr-2"><strong>Period</strong></v-chip>
										<span>{{ period }}</span>
									</v-col>

									<!-- Region  -->
									<v-col cols="12" sm="6" md="4" v-if="region">
										<v-chip label class="mr-2"><strong>Region</strong></v-chip>
										<span>{{ region }}</span>
									</v-col>

									<!-- Year  -->
									<v-col cols="12" sm="6" md="4" v-if="year">
										<v-chip label class="mr-2"><strong>Year</strong></v-chip>
										{{ year }}
									</v-col>

									<!-- Brick  -->
									<v-col cols="12" sm="6" md="4" v-if="brick">
										<v-chip label class="mr-2"><strong>Brick</strong></v-chip>
										<span>{{ brick }}</span>
									</v-col>

									<!-- Product  -->
									<v-col cols="12" sm="6" md="4" v-if="product">
										<v-chip label class="mr-2"><strong>Product</strong></v-chip>
										<span>{{ product }}</span>
									</v-col>

									<!-- Measurement  -->
									<v-col cols="12" sm="6" md="4" v-if="measurement">
										<v-chip label class="mr-2"><strong>Measurement</strong></v-chip>
										<span>{{ measurement }}</span>
									</v-col>

									<!-- Month  -->
									<v-col cols="12" sm="6" md="4" v-if="month">
										<v-chip label class="mr-2"><strong>Month</strong></v-chip>
										<span>{{ month }}</span>
									</v-col>

									<!-- Sales Channel  -->
									<v-col cols="12" sm="6" md="4" v-if="salesChannel">
										<v-chip label class="mr-2"><strong>Sales channel</strong></v-chip>
										<span>{{ salesChannel }}</span>
									</v-col>
								</v-row>
							</v-container>
						</v-card-text>
					</v-card>
				</v-dialog>

				<!-- Delete dialog -->
				<v-dialog v-model="dialogDelete" max-width="500px">
					<!-- <OverlayCircular :absolute="true" :value="true" /> -->

					<v-card v-if="!isLoading.deletingSavedSearch">
						<v-card-title class="text-h6 justify-center"
							>Are you sure you want to delete this item?</v-card-title
						>
						<v-card-actions class="pb-4">
							<v-spacer></v-spacer>
							<v-btn color="primary" outlined @click="closeDelete">No</v-btn>
							<v-btn color="primary" @click="deleteItemConfirm">Yes</v-btn>
							<v-spacer></v-spacer>
						</v-card-actions>
					</v-card>

					<v-card v-if="isLoading.deletingSavedSearch">
						<v-container style="height: 110px">
							<v-row class="fill-height" align-content="center" justify="center">
								<v-col class="text-subtitle-1 text-center mt-4" cols="12"> Deleting item... </v-col>
								<v-col cols="6">
									<v-progress-linear
										color="primary"
										indeterminate
										rounded
										height="6"
									></v-progress-linear>
								</v-col>
							</v-row>
						</v-container>
					</v-card>
				</v-dialog>
			</template>

			<template v-slot:[`item.actions`]="{ item }">
				<v-icon dense class="mr-2" @click="showItem(item)"> mdi-eye </v-icon>
				<v-icon dense class="mr-2" @click="editItem(item)"> mdi-pencil </v-icon>
				<v-icon dense class="mr-2" @click="searchItem(item)">mdi-layers-search</v-icon>
				<v-icon dense @click="deleteItem(item)"> mdi-delete </v-icon>
			</template>
		</v-data-table>
	</v-card>
</template>

<script>
	import SavedSearchAPI from '@/api/savedSearch.js';
	import utils from '@/utils.js';
	import { eventBus } from '@/eventBus.js';
	import Snackbar from './Snackbar.vue';
	import OverlayCircular from './OverlayCircular.vue';

	/**
	 * @module SavedSearch
	 *
     * @vue-data {object} [snackbar={}] - Display message to user
	 * @vue-data {boolean} [snackbar.status=false] - Control whether to show/hide the snackbar 
	 * @vue-data {string} [snackbar.text=''] - Sets the snackbar message  
	 * @vue-data {string} [snackbar.color=''] - Sets the color of the snackbar
     * 
     * @vue-data {object} [isLoading={}] - Display progress
     * @vue-data {boolean} [isLoading.savedSearches=false] - Display progress when loading saved searches 
	 * @vue-data {boolean} [isLoading.deletingSavedSearch=false] - Display progress when deleting a saved search 
     * 
	 * @vue-data {boolean} dialogShow=false - Render dialog show modal
	 * @vue-data {boolean} dialogEdit=false - Render dialog edit modal
	 * @vue-data {boolean} dialogDelete=false - Render dialog delete modal
     * 
	 * @vue-data {array<object>} [headers=[{ text: 'Name', align: 'start', value: 'search_name' }, { text: 'Created', value: 'created_at' }, { text: 'Updated', value: 'updated_at' }, { text: 'Actions', value: 'actions', sortable: false }]] - Define datatable headers
	 * @vue-data {array<object>} [savedSearches=[]] - Store/format saved searches so we can show it in the datatable
	 * @vue-data {number} editedIndex=-1 - not sure what this does... yet..
     * 
	 * @vue-data {object} [editedItem={}] - Current edited saved search
	 * @vue-data {string|number} [editedItem.id=0] - The edited saved search ID 
	 * @vue-data {number} [editedItem.search_name=''] - Search name of the edited saved search 
	 * @vue-data {object} [editedItem.search_params={}] - Search parameters of the edited saved search
	 * @vue-data {string} [editedItem.created_at=''] - Created at 
     * 
	 * @vue-data {object} [item={}] - Current saved search 
	 * @vue-data {string|number} [item.id=0] - Saved search ID 
	 * @vue-data {string} [item.search_name=''] - Search name of the saved search 
	 * @vue-data {object} [item.search_params={}] - Search parameters of the saved search
	 * @vue-data {boolean} [item.editing=false] - Edit saved search
	 * @vue-data {boolean} [item.searching=false] - Search saved search
	 * @vue-data {string} [item.created_at=''] -  Created at 
     * 
	 * @vue-data {object} [defaultItem={}] - Default saved search item 
     * @vue-data {string|number} [defaultItem.id=0] - Item ID 
	 * @vue-data {string} [defaultItem.search_name=''] - Item search name
	 * @vue-data {object} [defaultItem.search_params={}] - Item search parameters
	 * @vue-data {boolean} [defaultItem.editing=false] - Edit item
	 * @vue-data {boolean} [defaultItem.searching=false] - Search item
	 * @vue-data {string} [defaultItem.created_at=''] - Created at 
     * 
	 * @vue-computed {string} year - Display selected years for corresponding saved search
	 * @vue-computed {string} month - Display selected months for corresponding saved search
	 * @vue-computed {string} period - Display selected periods for corresponding saved search
	 * @vue-computed {string} country - Display selected countries for corresponding saved search
	 * @vue-computed {string} region - Display selected regions for corresponding saved search
	 * @vue-computed {string} brick - Display selected bricks for corresponding saved search
	 * @vue-computed {string} product - Display selected products for corresponding saved search
	 * @vue-computed {string} atcCode - Display selected ATC Codes for corresponding saved search
	 * @vue-computed {string} vnr - Display selected vnrs for corresponding saved search
	 * @vue-computed {string} measurement - Display selected measurements for corresponding saved search
	 * @vue-computed {string} salesChannel - Display selected sales channels for corresponding saved search
	 *
	 * @vue-event {function} update-saved-search - Update saved search datatable when a saved search gets updated
	 * @vue-event {function} save-search - Update saved search datatable when a saved search gets created 
	 */

	export default {
		name: 'SavedSearch',
		components: { Snackbar, OverlayCircular },
		data: () => ({
			snackbar: {
				status: false,
				text: '',
				color: '',
			},
			isLoading: {
				savedSearches: true,
				deletingSavedSearch: false,
			},
			dialogShow: false,
			dialogEdit: false,
			dialogDelete: false,
			headers: [
				{ text: 'Name', align: 'start', value: 'search_name' },
				{ text: 'Created', value: 'created_at' },
				{ text: 'Updated', value: 'updated_at' },
				{ text: 'Actions', value: 'actions', sortable: false },
			],
			savedSearches: [],
			editedIndex: -1,
			editedItem: {
				created_at: '',
				id: 0,
				search_name: '',
				search_params: {},
			},
			item: {
				created_at: '',
				id: 0,
				search_name: '',
				search_params: {},
				editing: false,
				searching: false,
			},
			defaultItem: {
				created_at: '',
				id: 0,
				search_name: '',
				search_params: {},
				editing: false,
				searching: false,
			},
		}),
		mounted() {
			this.getSavedSearches();
		},
		created() {
			// Update saved searches in datatable when saving or updating a search
			eventBus.$on('update-saved-search', () => this.getSavedSearches());
			eventBus.$on('save-search', () => this.getSavedSearches());
		},
		beforeDestroy() {
			// Remove specified event
			eventBus.$off('update-saved-search');
			eventBus.$off('save-search');
		},

		/**
		 * Listen for value changes in dialogShow, dialogEdit and dialogDelete
		 *
		 * @property {function} dialogShow - Open/close dialog modal
		 * @property {function} dialogEdit - Open/close dialog modal
		 * @property {function} dialogDelete - Open/close dialog modal
		 */
		watch: {
			dialogShow(val) {
				val || this.closeShow();
			},
			dialogEdit(val) {
				val || this.close();
			},
			dialogDelete(val) {
				val || this.closeDelete();
			},
		},
		computed: {
			year() {
				return this.commaSeperate(this.item.search_params, this.item.search_params.year);
			},
			month() {
				return this.commaSeperate(this.item.search_params, this.item.search_params.month);
			},
			period() {
				return this.commaSeperate(this.item.search_params, this.item.search_params.period);
			},
			country() {
				return this.commaSeperate(this.item.search_params, this.item.search_params.country);
			},
			region() {
				return this.commaSeperate(this.item.search_params, this.item.search_params.region);
			},
			brick() {
				return this.commaSeperate(this.item.search_params, this.item.search_params.brick);
			},
			product() {
				return this.commaSeperate(this.item.search_params, this.item.search_params.product);
			},
			atcCode() {
				return this.commaSeperate(this.item.search_params, this.item.search_params.atc_codes_h);
			},
			vnr() {
				return this.commaSeperate(this.item.search_params, this.item.search_params.vnrs_h);
			},
			measurement() {
				return this.commaSeperate(this.item.search_params, this.item.search_params.measurement);
			},
			salesChannel() {
				return this.commaSeperate(this.item.search_params, this.item.search_params.sales_channel_h);
			},
		},
		methods: {
			/**
			 * Separate each value with a comma
             * 
             * @param {object} obj - Saved search parameters 
             * @param {array} arr - Saved search properties
			 *
			 * @returns {string} - String with comma between values
			 */
			commaSeperate(obj, arr) {
				if (!utils.isEmptyObject(obj)) {
					if (arr && arr.length) {
						if (utils.isObjectInArray(arr)) {
							return arr.map(el => el.text).join(', ');
						} else {
							return arr.join(', ');
						}
					}
				}
			},

			/**
			 * @async
			 *
			 * @returns {void}
			 *
			 * @description
			 * Load saved searches
			 *
			 * - Store and format saved searches
			 */
			async getSavedSearches() {
				try {
					const { data } = await SavedSearchAPI.index();

					this.savedSearches = data.savedSearches.map(item => {
						return {
							id: item.id,
							search_name: item.search_name,
							search_params: JSON.parse(item.search_params),
							created_at: item.created_at,
							updated_at: item.updated_at,
							editing: false,
							searching: false,
							pivotData: [],
							pivotConfig: {
								cols: JSON.parse(item.search_params).pivot_cols,
								rows: JSON.parse(item.search_params).pivot_rows,
							},
						};
					});
				} catch (error) {
					console.error(error);
				} finally {
					this.isLoading.savedSearches = false;
				}
			},

			/**
			 * Show selected saved search
			 *
			 * @param {object} item - Saved search item we want to display
			 */
			showItem(item) {
				this.dialogShow = true;
				this.item = Object.assign({}, item);
			},

			/**
			 * Edit selected saved search
			 *
			 * @param {object} item - Saved search item we want to edit
			 */
			editItem(item) {
				this.$emit('edit-saved-search', Object.assign({}, item, (item.editing = true)));
				this.$emit('close-dialog-index', false);
				item.editing = false;
			},

			/**
			 * Search the saved search
			 *
			 * @param {object} item - Saved search we want to search for in data warehouse
			 */
			searchItem(item) {
				this.$emit(
					'submit-saved-search',
					Object.assign({}, item, ((item.searching = true), (item.editing = true)))
				);
				this.$emit('close-dialog-index', false);
				item.searching = false;
				item.editing = false;
			},

			/**
			 * Open modal to delete selected saved search
			 *
			 * @param {object} item - Saved search item we want to delete
			 */
			deleteItem(item) {
				this.editedIndex = this.savedSearches.indexOf(item);
				this.editedItem = Object.assign({}, item);
				this.dialogDelete = true;
			},

			/**
			 * @returns {void}
			 *
			 * @description
			 * Delete saved search
			 *
			 * - Display progress bar
			 * <br>&nbsp;
			 * - @HTTP 200
			 * - Deletes the selected saved search
			 * - Close delete modal
			 * <br>&nbsp;
			 * - @HTTP 500
			 * - Display error message
			 */
			deleteItemConfirm() {
				this.isLoading.deletingSavedSearch = true;
				SavedSearchAPI.destroy(this.editedItem.id)
					.then(response => {
						if (response.status === 200) {
							this.savedSearches.splice(this.editedIndex, 1);
							this.closeDelete();
						}
					})
					.catch(error => {
						this.snackbar = {
							status: true,
							text: 'Oops, something went wrong!',
							color: 'error',
						};

						console.error(error);
					})
					.finally(() => (this.isLoading.deletingSavedSearch = false));
			},

			/**
			 * Close saved search datatable
			 */
			closeIndex() {
				this.$emit('close-dialog-index', false);
			},

			/**
			 * Close saved search show modal
			 */
			closeShow() {
				this.dialogShow = false;
			},

			/**
			 * Close saved search edit modal
			 */
			close() {
				this.dialogEdit = false;
				this.$nextTick(() => {
					this.editedItem = Object.assign({}, this.defaultItem);
					this.editedIndex = -1;
				});
			},

			/**
			 * Close saved search delete modal
			 */
			closeDelete() {
				this.dialogDelete = false;
				this.$nextTick(() => {
					this.editedItem = Object.assign({}, this.defaultItem);
					this.editedIndex = -1;
				});
			},
		},
	};
</script>
